import {
  createMap,
  getLastOpenedMapId,
  getMap,
  getPreferences,
  listMaps,
  setLastOpenedMapId,
} from "@/src/core/persistence/repository";
import { migrateLegacyLocalStorage } from "@/src/core/persistence/legacyMigration";
import type { MapSummary, PersistedMap } from "@/src/core/persistence/schema";
import { useMapStore } from "@/src/store/useMapStore";
import { t } from "@/src/i18n";
import type { MapDocument } from "@/src/types/map";

const openRecord = async (record: PersistedMap) => {
  useMapStore.getState().openPersistedMap({
    id: record.id,
    document: record.document,
    viewport: record.viewport,
    nextRoomNumber: record.nextRoomNumber,
  });
  await setLastOpenedMapId(record.id);
};

/**
 * Decides what the editor is looking at on boot:
 * legacy localStorage map -> last opened -> most recent -> today's default new map.
 */
const runBootstrap = async (): Promise<void> => {
  const store = useMapStore.getState();
  try {
    const rescued = await migrateLegacyLocalStorage();

    const preferences = await getPreferences();
    if (preferences) store.applyPreferences(preferences);

    if (rescued) {
      await openRecord(rescued);
      return;
    }

    const lastOpenedId = await getLastOpenedMapId();
    if (lastOpenedId) {
      const record = await getMap(lastOpenedId);
      if (record) {
        await openRecord(record);
        return;
      }
    }

    const [mostRecent] = await listMaps();
    if (mostRecent) {
      const record = await getMap(mostRecent.id);
      if (record) {
        await openRecord(record);
        return;
      }
    }

    // Fresh visitor: keep the default new-map behaviour, then adopt it as map #1.
    // The store's module-init defaults are English literals because t() cannot
    // run before the store exists; localise them now that preferences are loaded.
    useMapStore.getState().setTitle(t("doc.defaultTitle"));
    useMapStore.getState().setAnnotationText(t("doc.defaultAnnotation"));
    const current = useMapStore.getState();
    const record = await createMap(current.getDocument(), current.viewport, current.nextRoomNumber);
    useMapStore.getState().setCurrentMapId(record.id);
    await setLastOpenedMapId(record.id);
  } finally {
    // Even if persistence is unavailable the editor must still be usable.
    useMapStore.getState().markHydrated();
  }
};

let bootstrapPromise: Promise<void> | null = null;

/**
 * Idempotent by construction. React StrictMode mounts effects twice in dev, and
 * without this guard a fresh visitor gets two "first" maps created in parallel.
 */
export const bootstrapSession = (): Promise<void> => {
  bootstrapPromise ??= runBootstrap();
  return bootstrapPromise;
};

/** Test hook: forget that bootstrap already ran. */
export const resetBootstrapForTests = () => {
  bootstrapPromise = null;
};

export const openMapById = async (id: string): Promise<boolean> => {
  const record = await getMap(id);
  if (!record) return false;
  await openRecord(record);
  return true;
};

/** Blank map that keeps the current palette/grid settings, mirroring the old "New map". */
export const createAndOpenBlankMap = async (): Promise<PersistedMap> => {
  const { meta, meta: { gridType } } = useMapStore.getState();
  const document: MapDocument = {
    version: "1.0.0",
    meta: { ...meta, title: gridType === "hex" ? "Untitled Hexcrawl" : "Untitled Dungeon" },
    geometry: { floorPolygons: [], hexCells: [] },
    objects: [],
  };
  const record = await createMap(document, { x: 0, y: 0, zoom: 1 }, 1);
  await openRecord(record);
  return record;
};

/** Adds an imported document to the library as its own map and opens it. */
export const importDocumentAsMap = async (document: MapDocument): Promise<PersistedMap> => {
  const record = await createMap(document, { x: 0, y: 0, zoom: 1 }, 1);
  await openRecord(record);
  return record;
};

export const refreshLibrary = (): Promise<MapSummary[]> => listMaps();
